import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Search, Trash2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useProducts } from "@/lib/hooks/use-products";
import type { Product } from "@/components/settings/ProductsSettings";
import {
  type DealLineItem,
  lineItemFromProduct,
  lineItemsTotal,
} from "@/hooks/useDealProducts";
import { cn } from "@/lib/utils";
import { displayNumber } from "@/lib/i18n/display";

interface Props {
  items: DealLineItem[];
  onChange: (next: DealLineItem[]) => void;
  /** Notify parent when total changes — used by AddDealDialog to seed amount. */
  onTotalChange?: (total: number) => void;
}

export default function DealProductsPicker({ items, onChange, onTotalChange }: Props) {
  const { t } = useTranslation();
  const { products: catalog } = useProducts();
  const { currency } = useCurrency();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const total = useMemo(() => lineItemsTotal(items), [items]);

  const formatPrice = (v: number) =>
    `${currency.symbol}${displayNumber(v, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const taken = new Set(items.map(i => i.productId));
    return catalog
      .filter(p => !taken.has(p.id))
      .filter(p => !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      .sort((a, b) => {
        const aHasPrice = a.listPrice != null && a.listPrice > 0 ? 1 : 0;
        const bHasPrice = b.listPrice != null && b.listPrice > 0 ? 1 : 0;
        if (aHasPrice !== bHasPrice) return bHasPrice - aHasPrice;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 8);
  }, [catalog, search, items]);

  const addProduct = (p: Product) => {
    const next = [...items, lineItemFromProduct(p)];
    onChange(next);
    onTotalChange?.(lineItemsTotal(next));
    setSearch("");
  };

  const updateLine = (idx: number, patch: Partial<DealLineItem>) => {
    const next = items.map((li, i) => (i === idx ? { ...li, ...patch } : li));
    onChange(next);
    onTotalChange?.(lineItemsTotal(next));
  };

  const removeLine = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    onTotalChange?.(lineItemsTotal(next));
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm">{t("grid.products.label")}</Label>
      {items.length > 0 && (
        <div className="flex items-center justify-end">
          <span className="text-xs text-muted-foreground">
            {t("grid.products.total")}{" "}<span className="font-medium text-foreground">{formatPrice(total)}</span>
          </span>
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded-md border border-border divide-y divide-border">
          {items.map((li, idx) => (
            <div key={`${li.productId}-${idx}`} className="flex items-center gap-2 px-2.5 py-2">
              <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm flex-1 truncate" title={li.name}>{li.name}</span>
              <Input
                type="number"
                min={1}
                value={li.quantity}
                onChange={e => updateLine(idx, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                className="h-7 w-14 text-xs text-center"
                aria-label={t("grid.products.quantity")}
              />
              <span className="text-xs text-muted-foreground">×</span>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{currency.symbol}</span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={li.price}
                  onChange={e => updateLine(idx, { price: Math.max(0, Number(e.target.value) || 0) })}
                  className="h-7 w-24 text-xs pl-5"
                  aria-label={t("grid.products.price")}
                />
              </div>
              <span className="text-xs font-medium w-20 text-right tabular-nums">
                {formatPrice(li.price * li.quantity)}
              </span>
              <button
                type="button"
                onClick={() => removeLine(idx)}
                className="p-1 text-muted-foreground hover:text-destructive transition"
                aria-label={t("grid.products.removeProduct")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t("grid.products.searchPlaceholder")}
          className="pl-9 h-9 text-sm"
        />
        {open && filtered.length > 0 && (
          <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-md bg-popover shadow-lg max-h-56 overflow-y-auto">
            {filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => addProduct(p)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex items-center gap-2"
                )}
              >
                <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1 truncate">{p.name}</span>
                {p.sku && <span className="text-xs text-muted-foreground">{p.sku}</span>}
                {p.listPrice !== null && (
                  <span className="text-xs text-muted-foreground">{formatPrice(p.listPrice)}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {open && filtered.length === 0 && search.trim() && (
          <div className="absolute z-10 top-full mt-1 w-full border border-border rounded-md bg-popover shadow-lg px-3 py-2 text-xs text-muted-foreground">
            {t("grid.products.noMatching")}
          </div>
        )}
      </div>
    </div>
  );
}